import * as React from "react";
import { useState, useEffect, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Text } from "@fluentui/react-components";
import { SpreetailHeader } from "../layout/SpreetailHeader";
import AIInsightsPanel from "./AIInsightsPanel";
import NoDataSelected from "./NoDataSelected";
import { getIdToken, saveInsightsRun, getInsightsRun } from "../../../shared/apiService";
import { handleTokenExpiration } from "../../../shared/authUtils";
import { DEFAULT_PROMPT } from "../../../shared/constants";
import { config } from "../../../shared/config";
const API_BASE_URL = config.apiBaseUrl;

interface InsightsData {
  htmlContent?: string; // Raw HTML from backend
}

const AIInsightsContainer: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [insightsData, setInsightsData] = useState<InsightsData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedSKUs, setSelectedSKUs] = useState<string[]>([]);
  const [userPrompt, setUserPrompt] = useState<string | null>(null);
  const [shortNameFromUrl, setShortNameFromUrl] = useState<string | null>(null);
  const [isFromHistory, setIsFromHistory] = useState<boolean>(false);
  
  // Use ref to track if fetch is in progress or completed
  const fetchStateRef = useRef<{
    isInProgress: boolean;
    hasCompleted: boolean;
    abortController: AbortController | null;
  }>({
    isInProgress: false,
    hasCompleted: false,
    abortController: null,
  });

  useEffect(() => {
    let isCancelled = false;

    const loadData = async () => {
      // Check if a fetch is already in progress or has completed for this location
      if (fetchStateRef.current.isInProgress || fetchStateRef.current.hasCompleted) {
        console.log("⏭️ Skipping duplicate fetch - already in progress or completed");
        return;
      }

      const urlParams = new URLSearchParams(location.search);
      const noData = urlParams.get("no_data") === "true";
      const dataKey = urlParams.get("data_key");
      const skusParam = urlParams.getAll("skus");
      const promptParam = urlParams.get("user_prompt");
      const shortNameParam = urlParams.get("short_name");
      const runId = urlParams.get("run_id");

      setSelectedSKUs(skusParam);
      setUserPrompt(promptParam);
      setShortNameFromUrl(shortNameParam);

      if (isCancelled) return;

      if (runId) {
        setIsFromHistory(true);
        try {
          setIsLoading(true);
          setError(null);

          const run = await getInsightsRun(runId);

          if (isCancelled) return;

          if (run.prompt) {
            setUserPrompt(run.prompt);
          }
          if (run.shortName) {
            setShortNameFromUrl(run.shortName);
          }

          const insightsPayload = (run as any).insights || (run as any).insights_payload;
          if (insightsPayload) {
            setInsightsData(insightsPayload);
            setIsLoading(false);
            return;
          }

          console.warn("Insights run loaded but no insights payload found for run_id:", runId);
          setError("no_data");
          setIsLoading(false);
        } catch (error) {
          if (isCancelled) return;
          console.error("Failed to load stored insights run:", error);
          setError("Failed to load insights. Please try again.");
          setIsLoading(false);
        }
        return;
      }

      if (noData) {
        setIsLoading(false);
        return;
      }

      if (!dataKey) {
        console.warn("No data_key parameter found in URL. Table data is required for AI Insights.");
        setIsLoading(false);
        setError("no_data");
        return;
      }

      if (dataKey) {
        try {
          const insightsCacheKey = `ai-insights-cache-${dataKey}`;
          const cachedInsights = sessionStorage.getItem(insightsCacheKey);
          if (cachedInsights) {
            console.log("Retrieved cached insights data from sessionStorage");
            const parsedInsights = JSON.parse(cachedInsights);
            setInsightsData(parsedInsights);
            setIsLoading(false);
            return;
          }
        } catch (error) {
          console.error("Failed to retrieve cached insights:", error);
        }
      }

      let tableData: { headers: string[]; rows: any[][] } | null = null;
      if (dataKey) {
        try {
          const storedData = sessionStorage.getItem(dataKey);
          if (storedData) {
            tableData = JSON.parse(storedData);
            console.log("✅ Retrieved table data from sessionStorage:", {
              key: dataKey,
              headers: tableData.headers?.length || 0,
              rows: tableData.rows?.length || 0,
            });

            if (
              !tableData.headers ||
              !Array.isArray(tableData.headers) ||
              tableData.headers.length === 0
            ) {
              console.error("❌ Invalid headers in retrieved data:", tableData);
              setIsLoading(false);
              setError("no_data");
              return;
            }
            if (!tableData.rows || !Array.isArray(tableData.rows) || tableData.rows.length === 0) {
              console.error("❌ Invalid rows in retrieved data:", tableData);
              setIsLoading(false);
              setError("no_data");
              return;
            }
          } else {
            console.warn("⚠️ Data key provided but no data found in sessionStorage:", dataKey);
            setIsLoading(false);
            setError("no_data");
            return;
          }
        } catch (error) {
          console.error("❌ Failed to retrieve table data from sessionStorage:", error);
          setIsLoading(false);
          setError("no_data");
          return;
        }
      }

      if (isCancelled) return;

      // Mark fetch as in progress and create abort controller
      fetchStateRef.current.isInProgress = true;
      const abortController = new AbortController();
      fetchStateRef.current.abortController = abortController;

      try {
        await fetchAndRenderInsights(
          promptParam,
          tableData,
          dataKey,
          shortNameParam,
          abortController.signal
        );
        
        if (!isCancelled) {
          fetchStateRef.current.hasCompleted = true;
        }
      } finally {
        fetchStateRef.current.isInProgress = false;
      }
    };

    loadData();

    return () => {
      isCancelled = true;
      // Abort any in-progress fetch
      if (fetchStateRef.current.abortController) {
        fetchStateRef.current.abortController.abort();
      }
      // Reset state when location changes
      fetchStateRef.current = {
        isInProgress: false,
        hasCompleted: false,
        abortController: null,
      };
    };
  }, [location.search]);

  const fetchAndRenderInsights = async (
    userPrompt: string | null,
    tableData: { headers: string[]; rows: any[][] } | null = null,
    dataKey: string | null = null,
    shortNameFromQuery: string | null = null,
    signal?: AbortSignal
  ) => {
    try {
      setIsLoading(true);
      setError(null);

      const token = await getIdToken();
      const apiUrl = `${API_BASE_URL}/summary`;

      if (
        !tableData ||
        !tableData.headers ||
        tableData.headers.length === 0 ||
        !tableData.rows ||
        tableData.rows.length === 0
      ) {
        const errorMsg =
          'No table data available. Please select a range of data in Excel (including headers) and click "Generate AI Insights" again.';
        console.error(errorMsg);
        setIsLoading(false);
        setError("no_data");
        return;
      }

      const requestBody = {
        table_data: {
          headers: tableData.headers,
          rows: tableData.rows,
        },
        user_prompt: userPrompt || DEFAULT_PROMPT,
      };

      console.log("📤 Sending table data to API:", {
        headers: tableData.headers.length,
        rows: tableData.rows.length,
        hasUserPrompt: !!userPrompt,
      });

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json, text/plain",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(requestBody),
        signal,
      });

      // Check if request was aborted
      if (signal?.aborted) {
        console.log("Request was aborted");
        return;
      }

      if (response.status === 401) {
        await handleTokenExpiration("Token expired during AI Insights fetch");
        throw new Error("Token is not configured. Please login again.");
      }

      if (!response.ok) {
        let errorDetail = "Failed to generate insights. Please try again.";
        try {
          const errorData = await response.json();
          if (typeof errorData.detail === "string") {
            errorDetail = errorData.detail;
          }
        } catch {
          // If JSON parse fails, use generic message
        }
        console.error("API Error Response:", errorDetail);
        throw new Error(errorDetail);
      }

      const contentType = response.headers.get("content-type");
      let apiData;

      if (contentType && contentType.includes("application/json")) {
        apiData = await response.json();
        console.log("✅ JSON data received from API");
      } else {
        apiData = await response.text();
        console.log("✅ Text/HTML data received from API");
      }

      if (apiData) {
        const insights: InsightsData = {
          htmlContent: typeof apiData === "string" ? apiData : JSON.stringify(apiData, null, 2),
        };

        // Update UI immediately - don't wait for anything else
        setInsightsData(insights);
        setIsLoading(false);
        console.log("✅ UI updated with insights data");

        // Cache for reload (non-blocking, fire-and-forget)
        if (dataKey) {
          try {
            const insightsCacheKey = `ai-insights-cache-${dataKey}`;
            sessionStorage.setItem(insightsCacheKey, JSON.stringify(insights));
            console.log("✅ Cached insights data in sessionStorage");
          } catch (error) {
            console.warn("Failed to cache insights data:", error);
          }
        }

        // Save to history in background (completely non-blocking)
        // Don't await this - let it run in the background
        saveToHistory(userPrompt, shortNameFromQuery, tableData, insights).catch((error) => {
          console.warn("⚠️ Failed to save insights run (non-critical):", error);
        });
      } else {
        throw new Error("No data received from API");
      }
    } catch (error: any) {
      if (error.name === "AbortError") {
        console.log("✋ Fetch aborted - duplicate call prevented");
        return;
      }
      console.error("Failed to load insights:", error);
      const message = error instanceof Error ? error.message : "Failed to generate insights.";
      setError(message);
      setIsLoading(false);
    }
  };

  const saveToHistory = async (
    userPrompt: string | null,
    shortNameFromQuery: string | null,
    tableData: { headers: string[]; rows: any[][] },
    insights: InsightsData
  ) => {
    const historyPrompt = userPrompt || DEFAULT_PROMPT;
    const effectiveShortName =
      (shortNameFromQuery && shortNameFromQuery.trim()) ||
      (shortNameFromUrl && shortNameFromUrl.trim()) ||
      "";

    let shortName: string;
    if (effectiveShortName) {
      shortName = effectiveShortName;
    } else if (historyPrompt.trim() === DEFAULT_PROMPT.trim()) {
      shortName = "Default Prompt";
    } else {
      shortName = (historyPrompt.split("\n")[0] || "Insights run").slice(0, 80);
    }

    await saveInsightsRun({
      prompt: historyPrompt,
      default_prompt: DEFAULT_PROMPT,
      shortName,
      table_data: {
        headers: tableData.headers,
        rows: tableData.rows,
      },
      insights,
    });
    console.log("✅ Saved insights run to insights history");
  };

  const handleGoBack = () => {
    history.push("/edit-prompt");
  };

  const urlParams = new URLSearchParams(location.search);
  const noData = urlParams.get("no_data") === "true";

  if (noData || error === "no_data") {
    return <NoDataSelected />;
  }

  if (isLoading) {
    return (
      <>
        <style>
          {`
            .bg-gradient-custom {
              background: linear-gradient(180deg, #D3E8D9 0%, #EEEEEE 79.45%);
            }
          `}
        </style>
        <div className="h-screen flex flex-col relative overflow-hidden font-sans text-gray-800 bg-gradient-custom">
          <div
            className="absolute -right-10 -top-10 w-50 h-50 blur-[40px] pointer-events-none"
            style={{
              background:
                "radial-gradient(circle at center, rgba(134, 239, 172, 0.3), transparent 70%)",
            }}
          ></div>
          <div
            className="absolute -left-8 -bottom-8 w-38 h-38 blur-[40px] pointer-events-none"
            style={{
              background:
                "radial-gradient(circle at center, rgba(167, 243, 208, 0.3), transparent 70%)",
            }}
          ></div>

          <SpreetailHeader />

          <div className="flex flex-col items-center justify-center flex-1 gap-4 p-6">
            <div className="flex flex-col items-center gap-4">
              <div className="relative">
                <div className="w-16 h-16 border-4 rounded-full animate-spin" style={{ borderColor: "rgba(6,142,117,0.2)", borderTopColor: "#068E75" }}></div>
                <div
                  className="w-12 h-12 border-4 border-transparent rounded-full animate-spin absolute top-2 left-2"
                  style={{ borderTopColor: "rgba(6,142,117,0.5)", animationDirection: "reverse", animationDuration: "1.5s" }}
                ></div>
                <div className="w-8 h-8 rounded-full absolute top-4 left-4 flex items-center justify-center" style={{ backgroundColor: "#068E75" }}>
                  <div className="w-2 h-2 bg-white rounded-full"></div>
                </div>
              </div>
              <div className="text-center">
                <Text size={500} weight="semibold" style={{ color: "#0F172A" }}>
                  Processing Intelligence
                </Text>
                <div style={{ height: 6 }} />
                <Text size={300} style={{ color: "#667085" }}>
                  Analyzing data patterns and generating strategic insights
                </Text>
                <div className="flex justify-center gap-1 mt-4">
                  <div className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: "rgba(6,142,117,0.5)" }}></div>
                  <div
                    className="w-2 h-2 rounded-full animate-pulse"
                    style={{ backgroundColor: "rgba(6,142,117,0.5)", animationDelay: "0.2s" }}
                  ></div>
                  <div
                    className="w-2 h-2 rounded-full animate-pulse"
                    style={{ backgroundColor: "rgba(6,142,117,0.5)", animationDelay: "0.4s" }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (error && error !== "no_data") {
    return (
      <>
        <style>
          {`
            .bg-gradient-custom {
              background: linear-gradient(180deg, #D3E8D9 0%, #EEEEEE 79.45%);
            }
          `}
        </style>
        <div className="h-screen flex flex-col relative font-sans text-gray-800 bg-gradient-custom" style={{ overflow: "hidden" }}>
          <div
            className="absolute -right-10 -top-10 w-50 h-50 blur-[40px] pointer-events-none"
            style={{
              background: "radial-gradient(circle at center, rgba(134, 239, 172, 0.3), transparent 70%)",
            }}
          ></div>
          <div
            className="absolute -left-8 -bottom-8 w-38 h-38 blur-[40px] pointer-events-none"
            style={{
              background: "radial-gradient(circle at center, rgba(167, 243, 208, 0.3), transparent 70%)",
            }}
          ></div>
          <SpreetailHeader />

          <div className="flex-1 flex flex-col items-center justify-center px-5">
            <div className="flex flex-col items-center justify-center w-full" style={{ maxWidth: "420px" }}>
              <div style={{ marginBottom: "24px" }}>
                <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center">
                  <div className="w-8 h-8 bg-red-600 rounded-full flex items-center justify-center">
                    <span className="text-white text-lg font-bold">!</span>
                  </div>
                </div>
              </div>

              <div className="text-center" style={{ marginBottom: "32px" }}>
                <h2
                  style={{
                    fontSize: "18px",
                    fontWeight: 600,
                    lineHeight: "1.4",
                    color: "#1F2937",
                    marginBottom: "10px",
                    fontFamily: 'Inter, ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                    letterSpacing: "-0.01em",
                    marginTop: 0,
                  }}
                >
                  Something went wrong
                </h2>
                <p
                  style={{
                    fontSize: "13px",
                    fontWeight: 400,
                    lineHeight: "1.6",
                    color: "#6B7280",
                    fontFamily: 'Inter, ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                    margin: 0,
                    padding: "0 8px",
                  }}
                >
                  {error}
                </p>
                <p
                  style={{
                    fontSize: "12px",
                    fontWeight: 400,
                    lineHeight: "1.5",
                    color: "#9CA3AF",
                    fontFamily: 'Inter, ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                    margin: 0,
                    marginTop: "10px",
                    padding: "0 8px",
                  }}
                >
                  Make sure you have selected data in your Excel sheet before generating insights.
                </p>
              </div>

              <button
                onClick={handleGoBack}
                className="w-full text-white border-none rounded-md p-3 px-4 text-[13px] font-semibold transition-all duration-200 shadow-md flex items-center justify-center"
                style={{
                  backgroundColor: "#068E75",
                  boxShadow: "0 2px 4px rgba(6, 142, 117, 0.3)",
                  cursor: "pointer",
                  maxWidth: "380px",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#057A63";
                  e.currentTarget.style.transform = "translateY(-1px)";
                  e.currentTarget.style.boxShadow = "0 4px 8px rgba(6, 142, 117, 0.4)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "#068E75";
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "0 2px 4px rgba(6, 142, 117, 0.3)";
                }}
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <AIInsightsPanel
      insightsData={insightsData}
      selectedSKUs={selectedSKUs}
      userPrompt={userPrompt}
      onGoBack={handleGoBack}
      isFromHistory={isFromHistory}
    />
  );
};

export default AIInsightsContainer;